import { useContext, useEffect, useRef, useState } from 'react';
import React from 'react';

import { Button } from '@selina-finance/ui';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import { DebtLabelEnum } from '@src/app/formModels/eligibility/debtCon';
import {
    CreditCommitmentsData,
    DebtsToConsolidate,
} from '@src/app/interfaces/debtcon';
import { Offer } from '@src/app/interfaces/offer';
import { debtTypeConfig, getApplicantName } from '@src/shared/helpers/debtCon';
import { roundNumber, toCurrency } from '@src/shared/helpers/numbers';
import BackButton from '@src/ui/components/BackButton/BackButton';
import { HttpError } from '@src/ui/components/HttpError';
import CoinsIcon from '@src/ui/components/Icons/CoinsIcon';
import DebtCoinsHand from '@src/ui/components/Icons/DebtCoinsHand';
import PlusCircle from '@src/ui/components/Icons/PlusCircle';
import Question from '@src/ui/components/Icons/Question';
import TrendDown from '@src/ui/components/Icons/TrendDown';
import ProgressMenu from '@src/ui/components/Progress/ProgressMenu';
import ToggleSwitch from '@src/ui/components/ToggleSwitch/ToggleSwitch';
import ModalLayout from '@src/ui/layouts/ModalLayout';

import ReviewCalculationView from './ReviewCalculationView';

const ReviewView = () => {
    const navigate = useNavigate();
    const {
        creditCommitmentsData,
        consolidateCreditCommitments,
        setCurrentDebtIndex,
        toggleStates,
        setToggleStates,
        setReviewPageNav,
        debtsToConsolidate,
        setDebtsToConsolidate,
    } = useContext(DebtConContext);

    const { applicants, loanDetails, offers, selectedProduct } =
        useContext(ApplicationContext);

    const [error, setError] = useState<string | null>(null);
    const bottomRef = useRef(null);
    const [showFixedFooter, setShowFixedFooter] = useState(true);
    const [reviewCalculationModal, setReviewCalculationModal] = useState(false);

    useEffect(() => {
        setReviewPageNav(false);
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowFixedFooter(false);
                } else {
                    setShowFixedFooter(true);
                }
            },
            { root: null, threshold: 0.1 },
        );

        const currentRef = bottomRef.current;

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [setReviewPageNav]);

    const selectedProductDetails = offers.find(
        (offer: Offer) => offer.productCode === selectedProduct,
    );

    const initialPayment = selectedProductDetails
        ? roundNumber(selectedProductDetails.initialPayment)
        : null;

    const getDebtConfig = (type: DebtLabelEnum) =>
        debtTypeConfig[type] || {
            icon: <CoinsIcon />,
        };

    const filterConsolidatableDebts = (
        creditCommitmentsData: CreditCommitmentsData[],
        debtsToConsolidate: DebtsToConsolidate[] | null,
    ): CreditCommitmentsData[] => {
        if (!debtsToConsolidate) {
            return [];
        }

        return (
            creditCommitmentsData?.filter((debt) => {
                const debtState = debtsToConsolidate?.find(
                    (debtToConsolidate) => debtToConsolidate.id === debt.id,
                );
                return debtState?.consolidate;
            }) || []
        );
    };

    const consolidatableDebts = filterConsolidatableDebts(
        creditCommitmentsData,
        debtsToConsolidate,
    );

    const parseAmountToConsolidate = (
        amount: string | number | undefined | null,
    ): number => {
        if (typeof amount === 'number') {
            return roundNumber(amount);
        }
        const amountString = (amount?.toString() || '').replace(/,/g, '');
        const parsedAmount = parseFloat(amountString);
        return isNaN(parsedAmount) ? 0 : roundNumber(parsedAmount);
    };

    const totalMonthlyPayment = roundNumber(
        consolidatableDebts.reduce(
            (total: number, item: CreditCommitmentsData) =>
                total +
                parseAmountToConsolidate(item.monthlyPaymentCustomerVerified),
            0,
        ),
    );

    const hasToggledDebts = consolidatableDebts.length > 0;

    const estimatedChange = roundNumber(
        hasToggledDebts && initialPayment !== null
            ? totalMonthlyPayment - initialPayment
            : 0,
    );

    const totalConsolidation = roundNumber(
        consolidatableDebts.reduce(
            (total: number, item: CreditCommitmentsData) => {
                const amount = parseAmountToConsolidate(
                    item.amountToConsolidate,
                );
                return total + amount;
            },
            0,
        ),
    );

    const remainingLoan = roundNumber(loanDetails.amount - totalConsolidation);

    const estimatedMonthlyPayments = consolidatableDebts.map(
        (item: CreditCommitmentsData) => {
            const amount = parseAmountToConsolidate(item.amountToConsolidate);
            if (initialPayment !== null) {
                return roundNumber(
                    (initialPayment / loanDetails.amount) * amount,
                );
            }
            return 0;
        },
    );

    const totalEstimatedPayments = roundNumber(
        estimatedMonthlyPayments.reduce(
            (total, payment) => total + (isNaN(payment) ? 0 : payment),
            0,
        ),
    );

    const estimatedReductionInPayments = roundNumber(
        totalMonthlyPayment - totalEstimatedPayments,
    );

    const handleToggle = (index: number) => {
        setReviewPageNav(true);
        const currentDebt = creditCommitmentsData[index];

        const debtId = currentDebt.id;
        setCurrentDebtIndex(index);

        if (currentDebt.dffDebtConSkipped) {
            navigate('/eligibility/debt-con/input');
            return;
        }

        const updatedToggleStates: Record<string, boolean> = {
            ...toggleStates,
            [debtId]: !toggleStates[debtId],
        };
        setToggleStates(updatedToggleStates);

        const updatedDebtsToConsolidate = creditCommitmentsData.map(
            (debt: {
                id: { toString: () => string | number };
                declaration: any;
            }) => ({
                id: debt.id,
                declaration: debt.declaration,
                consolidate: updatedToggleStates[debt.id.toString()] || false,
            }),
        );
        setDebtsToConsolidate(updatedDebtsToConsolidate);
    };

    const handleCardClick = (index: number) => {
        setReviewPageNav(true);
        setCurrentDebtIndex(index);
        navigate('/eligibility/debt-con/input');
    };

    const saveDebtsToConsolidate = async () => {
        const response = await consolidateCreditCommitments();
        if (response.success) {
            navigate('/eligibility/wait', {
                replace: true,
                state: { debtCon: true },
            });
        } else {
            setError(response.error);
        }
    };

    return (
        <div className='flex flex-row justify-between'>
            <div className='flex flex-col mx-auto max-w-400 w-full sm:ml-0 mt-20 sm:mt-0 sm:mr-10'>
                <div className='text-steel-100 hidden lg:block mb-8'>
                    <BackButton />
                </div>
                <div className='text-xl'>
                    <div className='flex flex-col'>
                        <p className='text-2xl leading-100 font-sora-bold lg:text-28 tracking-tighter'>
                            Tell us what you’re consolidating
                        </p>
                        <p className='leading-6 mt-2 text-lg text-midnight-200'>
                            Select the debts you’d like to consolidate and
                            submit when you’re ready. Tap any of these to edit
                            details.
                        </p>

                        <div className='space-y-2 mt-6'>
                            {creditCommitmentsData.map(
                                (
                                    currentDebt: CreditCommitmentsData,
                                    index: number,
                                ) => {
                                    const debtType = currentDebt.type;
                                    const { icon } = getDebtConfig(
                                        debtType as DebtLabelEnum,
                                    );

                                    const amountToConsolidate =
                                        parseAmountToConsolidate(
                                            currentDebt.amountToConsolidate,
                                        );

                                    const applicantName = getApplicantName(
                                        applicants.length,
                                        currentDebt,
                                        applicants,
                                    );

                                    return (
                                        <div
                                            key={index}
                                            className='flex flex-col bg-white p-4 rounded-lg cursor-pointer'
                                            onClick={() =>
                                                handleCardClick(index)
                                            }
                                        >
                                            <div className='flex justify-between'>
                                                <div className='flex text-sm items-center'>
                                                    {icon}
                                                    <span className='ml-1'>
                                                        {currentDebt.dffDebtConSkipped
                                                            ? debtType
                                                            : currentDebt.lenderName ||
                                                              debtType}
                                                    </span>
                                                </div>

                                                <div className='flex items-center space-x-2'>
                                                    {applicantName && (
                                                        <span className='bg-almond-300 text-xs rounded-full px-2 inline-flex items-center'>
                                                            {applicantName}
                                                        </span>
                                                    )}
                                                    {currentDebt.ignore && (
                                                        <span className='bg-peppermint-500 text-green-900 text-xs rounded-full px-2 inline-flex items-center font-bold'>
                                                            Settled
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className='flex justify-between'>
                                                <div>
                                                    <div
                                                        className={`text-left ${
                                                            currentDebt.ignore
                                                                ? 'text-gray-300'
                                                                : ''
                                                        }`}
                                                    >
                                                        <span className='font-bold text-sm'>
                                                            £
                                                            {toCurrency(
                                                                parseAmountToConsolidate(
                                                                    currentDebt.monthlyPaymentCustomerVerified,
                                                                ),
                                                            )}
                                                        </span>
                                                        <span className='text-xs'>
                                                            /month
                                                        </span>
                                                    </div>
                                                    {currentDebt.interestRate &&
                                                        !currentDebt.ignore &&
                                                        !currentDebt.dffDebtConSkipped && (
                                                            <p className='text-xs mt-1.5'>
                                                                <span className='text-sm font-bold'>
                                                                    {
                                                                        currentDebt.interestRate
                                                                    }
                                                                    %
                                                                </span>{' '}
                                                                interest rate
                                                            </p>
                                                        )}
                                                </div>

                                                <div>
                                                    <p
                                                        className={`text-xs mb-2 mt-1.5 ${
                                                            currentDebt.ignore
                                                                ? 'text-gray-300'
                                                                : ''
                                                        }`}
                                                    >
                                                        <span className='text-sm font-bold mr-1'>
                                                            £
                                                            {toCurrency(
                                                                amountToConsolidate,
                                                            )}
                                                        </span>
                                                        balance
                                                    </p>
                                                    {toggleStates &&
                                                        !currentDebt.ignore && (
                                                            <ToggleSwitch
                                                                checked={
                                                                    toggleStates[
                                                                        currentDebt.id.toString()
                                                                    ] || false
                                                                }
                                                                onChange={() =>
                                                                    handleToggle(
                                                                        index,
                                                                    )
                                                                }
                                                                onClick={(
                                                                    event: React.MouseEvent,
                                                                ) =>
                                                                    event.stopPropagation()
                                                                }
                                                                className='ml-auto'
                                                            />
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                        </div>

                        <div
                            className='flex items-center mt-2 gap-1 cursor-pointer'
                            onClick={() =>
                                navigate('/eligibility/debt-con/new-input')
                            }
                        >
                            <PlusCircle />
                            <span className='text-xs font-bold'>Add more</span>
                        </div>

                        <div className='mt-6'>
                            <p className='text-2xl lg:text-28 font-sora-bold tracking-tighter'>
                                Summary
                            </p>
                            <div className='flex flex-wrap items-center bg-white rounded-lg gap-2 mt-2'>
                                <div className='flex justify-between items-center w-full mx-3 mt-2'>
                                    <span className='text-sm'>
                                        Total to consolidate
                                    </span>
                                    <span className='font-sora-bold text-sm_body'>
                                        £{toCurrency(totalConsolidation)}
                                    </span>
                                </div>
                                <div className='w-full h-0.5 bg-almond-100'></div>{' '}
                                <div className='flex justify-between items-center w-full mx-3 mb-2'>
                                    <span className='text-sm'>
                                        Remaining loan
                                    </span>
                                    <span className='font-sora-bold text-sm_body'>
                                        £{toCurrency(remainingLoan)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className='relative flex'>
                            <div ref={bottomRef}></div>
                            <div className='flex flex-wrap items-center mt-4 p-4 bg-midnight-500 rounded-lg w-full h-44 pb-8 text-periwinkle-200'>
                                <span className='text-lg pr-44 leading-6'>
                                    {estimatedReductionInPayments < 0
                                        ? 'Estimated monthly increase in debt payments'
                                        : 'Estimated monthly reduction in debt payments'}
                                </span>
                                <span className='font-sora-bold text-2xl lg:text-28 text-almond-100'>
                                    £
                                    {toCurrency(
                                        Math.abs(estimatedReductionInPayments),
                                    )}
                                </span>
                            </div>
                            <div className='absolute left-0 right-0 flex justify-end mt-8 p-3'>
                                <DebtCoinsHand />
                            </div>
                        </div>

                        <div
                            className='flex items-center mt-5 gap-1 cursor-pointer z-10 self-start'
                            onClick={() => {
                                setReviewCalculationModal(true);
                            }}
                        >
                            <Question />
                            <p className='text-xs font-bold'>
                                How we calculate this
                            </p>
                        </div>

                        {showFixedFooter && (
                            <div className='flex bg-monochrome-100 fixed bottom-0 left-0 right-0 z-50 p-6 shadow-sm text-sm transform rotate-180 sm:hidden'>
                                <div className='flex flex-1 justify-between transform rotate-180'>
                                    <div>
                                        <span className='text-sm'>
                                            Total to consolidate
                                        </span>
                                        <span className='font-bold block'>
                                            £{toCurrency(totalConsolidation)}
                                        </span>
                                    </div>
                                    <div>
                                        <span>
                                            {estimatedReductionInPayments < 0
                                                ? 'Estimated monthly increase'
                                                : 'Estimated monthly reduction'}
                                        </span>
                                        <div className='flex items-center'>
                                            <div
                                                className={`transform ${
                                                    estimatedReductionInPayments <
                                                    0
                                                        ? 'rotate-180'
                                                        : ''
                                                }`}
                                            >
                                                <TrendDown />
                                            </div>
                                            <span className='font-bold block ml-1 text-peppermint-700'>
                                                £
                                                {toCurrency(
                                                    Math.abs(
                                                        estimatedReductionInPayments,
                                                    ),
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <footer className='mt-6'>
                            <Button
                                size='large'
                                color='primary'
                                className='font-bold w-full text-lg'
                                onClick={saveDebtsToConsolidate}
                                disabled={
                                    !debtsToConsolidate?.some(
                                        (debt: any) => debt.consolidate,
                                    )
                                }
                            >
                                <span className='text-lg'>
                                    View my personalised quote
                                </span>
                            </Button>
                            <p className='text-sm text-midnight-200 leading-120% mt-4 text-center'>
                                Remember, if you consolidate your existing
                                borrowing, you may be extending the term and
                                increasing the amount you repay in total.
                            </p>
                            {error && <HttpError error={error} />}
                        </footer>
                    </div>
                </div>
            </div>
            <ProgressMenu />
            <ModalLayout
                open={reviewCalculationModal}
                onClose={() => setReviewCalculationModal(false)}
                contentClassName='bg-white max-w-3xl mx-auto'
            >
                <ReviewCalculationView
                    closeModal={() => setReviewCalculationModal(false)}
                    initialPayment={toCurrency(initialPayment || 0)}
                    totalMonthlyPayment={toCurrency(totalMonthlyPayment || 0)}
                    estimatedChange={estimatedChange}
                    loanAmount={toCurrency(loanDetails.amount || 0)}
                    totalConsolidation={toCurrency(totalConsolidation || 0)}
                    otherPurpose={toCurrency(remainingLoan || 0)}
                    totalEstimatedPayments={toCurrency(
                        totalEstimatedPayments || 0,
                    )}
                    estimatedReductionInPayments={estimatedReductionInPayments}
                />
            </ModalLayout>
        </div>
    );
};

export default ReviewView;
